import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { TextBand, bandBg } from "@/components/gisela/TextBand"
import {
  BuildingBand,
  GettingHereBand,
  LocationHero,
  RoomBand,
  VideoTourBand,
  fill,
  type HeroSlides,
  type Photo,
  type SiteRoom,
  type TourVideo,
} from "@/components/location/bands"
import { useLocalePath } from "@/lib/locale-path"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { HeroPhoto } from "@/components/gisela/HeroBand"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  /** The building as the venue's page has it, plus where that page lives. */
  site: {
    id: number
    name: string
    slug: string
    region: string
    address: string
    address_lines: string[]
    maps_url: string | null
    latitude: number | null
    longitude: number | null
    opening_hours: string | null
    rooms: SiteRoom[]
    /** The venue's own page and gallery for this location, on its domain. */
    venue_url: string
    gallery_url: string
  }
  /** The gallery's non-room photos — outside, reception, kitchen. */
  building: { photos: Photo[] }
  /** The newest video filed for this location, if any. */
  video?: TourVideo | null
  /** Hero photos — hers, or the venue page's until she places any. */
  hero?: HeroSlides
  content: {
    hero: { heading?: string; subheading?: string; _hidden?: boolean }
    intro: { body?: string; _hidden?: boolean }
    rooms: { heading?: string; body?: string; _hidden?: boolean }
    building: { heading?: string; body?: string; _hidden?: boolean }
    treats: { body?: string; _hidden?: boolean }
    who_comes: { body?: string; _hidden?: boolean }
    getting_here: { body?: string; directions?: string | null; photo?: PlacementPhoto | null; _hidden?: boolean }
    faq: { body?: string; _hidden?: boolean }
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  tile_photos?: Record<string, PlacementPhoto | null>
  contact_photo?: PlacementPhoto | null
  section_order?: string[]
  meta?: PageMeta
}

/**
 * Her page per town — the same building the venue shows at /<slug>, told
 * for someone looking for therapy rather than a room: the slideshow hero,
 * the rooms she works in with their photos, the video tour, the building,
 * then what she treats here, who travels in, getting here and the FAQ, and
 * the free-call band every page of hers closes on. The facts — rooms,
 * photos, address, directions — are the location's own; the words are hers.
 */
export default function TherapyInTown({
  site,
  building,
  video,
  hero = { slides: [], interval_seconds: 6 },
  content,
  tile_photos = {},
  contact_photo,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.town
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  const name = { name: site.name }
  const K = `therapy-in-${site.slug}`
  const title = meta?.title ?? fill(t.meta_title, name)
  const description = meta?.description ?? fill(t.meta_description, name)
  const faqMarkdown = { strong: ({ children }: { children?: React.ReactNode }) => <strong className="mb-1 block">{children}</strong> }

  const bands = presentBands(
    [
      { id: `${K}-intro`, present: !sectionHidden(content.intro) && !!content.intro.body,
        render: (i) => <TextBand index={i} heading={t.intro_heading} body={content.intro.body} /> },
      { id: `${K}-rooms`, present: !sectionHidden(content.rooms) && site.rooms.length > 0,
        render: (i) => (
          <section id="rooms" className={`${bandBg(i)} pt-16`}>
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <h2>{content.rooms.heading || t.rooms_heading}</h2>
              {content.rooms.body && (
                <div className="mt-3 max-w-3xl text-ink-muted [&_p]:mt-2 [&_p:first-child]:mt-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.rooms.body}</ReactMarkdown>
                </div>
              )}
            </div>
            {/* One band per room, its own gallery photos beside it — the
                gallery itself is on the venue's site. */}
            {site.rooms.map((room, index) => (
              <RoomBand key={room.id} room={room} gallery={site.gallery_url} flip={index % 2 === 1} first={index === 0} />
            ))}
          </section>
        ) },
      { id: `${K}-video-tour`, present: !!video && !!(video.embed_src || video.file_url),
        render: (i) => <VideoTourBand video={video!} heading={fill(t.tour_heading, name)} className={bandBg(i)} /> },
      { id: `${K}-building`, present: !sectionHidden(content.building) && !!(content.building.body || building.photos.length),
        render: (i) => (
          <BuildingBand
            heading={content.building.heading || t.building_heading}
            body={content.building.body}
            photos={building.photos}
            gallery={site.gallery_url}
            galleryLabel={fill(t.full_gallery, name)}
            className={bandBg(i)}
          />
        ) },
      { id: `${K}-treats`, present: !sectionHidden(content.treats) && !!content.treats.body,
        render: (i) => <TextBand index={i} heading={t.treats_heading} body={content.treats.body} /> },
      { id: `${K}-who-comes`, present: !sectionHidden(content.who_comes) && !!content.who_comes.body,
        render: (i) => <TextBand index={i} heading={t.who_comes_heading} body={content.who_comes.body} /> },
      { id: `${K}-getting-here`, present: !sectionHidden(content.getting_here),
        render: (i) => (
          <GettingHereBand
            site={site}
            heading={t.getting_here_heading}
            lead={content.getting_here.body}
            directions={content.getting_here.directions ?? undefined}
            photo={content.getting_here.photo}
            className={bandBg(i)}
          />
        ) },
      { id: `${K}-faq`, present: !sectionHidden(content.faq) && !!content.faq.body,
        render: (i) => <TextBand index={i} heading={tc.common_questions} body={content.faq.body} markdown={faqMarkdown} /> },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {!sectionHidden(content.hero) && (
          <LocationHero
            hero={hero}
            eyebrow={fill(t.eyebrow, name)}
            heading={content.hero.heading || fill(t.hero_heading, name)}
            subheading={content.hero.subheading}
            address={site.address}
          >
            {/* One ask on this page: the free call. */}
            <div className="mt-8">
              <a
                href={lp("/book")}
                className="inline-block rounded-full bg-white px-7 py-3 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
              >
                {props.translations.gisela_nav.book_call}
              </a>
            </div>
          </LocationHero>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}

        {!sectionHidden(content.cta) && (
          <ContactBand heading={fill(t.cta_heading, name)} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* The other audience, in one line: the building is also the venue's. */}
        <section className="bg-cream py-8">
          <div className="mx-auto max-w-3xl px-6 text-center lg:px-8">
            <p className="text-sm text-ink-muted">
              {fill(t.room_hire_note, name)}{" "}
              <a href={`${site.venue_url}`}>{t.room_hire_link}</a>
            </p>
          </div>
        </section>

        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand heading={content.contact?.heading} body={content.contact?.body} photo={contact_photo} />
        )}
      </PublicLayout>
    </>
  )
}
